import { createSelector } from 'reselect';
import { fromJS, List } from 'immutable';
import { STATE_NAME, States } from './constants';
import { getMarketName } from '../app/selectors';
import { MARKET_GLOBAL } from '../app/constants';
import { filterByStatus, getEnrichmentIds } from './util';
import { getPpsiById } from '../PpsiContainer/selectors';

const userMarket = JSON.parse(localStorage.getItem('localMarket'));

export const getStateSlice = state => state.get(STATE_NAME);

const getPpisMap = state => getStateSlice(state).get('ppis');
export const getSinglePpi = state => getStateSlice(state).get('singlePpi');

// const getMarketPpisMap = state => getStateSlice(state).get('marketPpis');

export const getGlobalVariantDialog = state =>
  getStateSlice(state).get('openGlobalPpiDialog');

export const getPpisAll = createSelector(
  getPpisMap,
  ppisMap => ppisMap.toList()
);

export const getPpisUpdates = state => getStateSlice(state).get('updatePpis');

export const getGlobalPpisUpdates = state =>
  getStateSlice(state).get('updateGlobalPpis');

// export const getMarketPpisAll = createSelector(
//   getMarketPpisMap,
//   ppisMap => ppisMap.toList()
// );

/**
 * Get PPIs matching current users market.
 */
export const getPpis = createSelector(
  getPpisAll,
  getMarketName,
  (ppis, marketName) => ppis.filter(ppi => ppi.get('market') === marketName)
);

/**
 * Get PPIs matching current users market.
 */

export const getMarketPpis = createSelector(
  getPpisAll,
  getMarketName,
  (ppis, marketName) => ppis.filter(ppi => ppi.get('market') === userMarket)
);

/**
 * Get global PPIs that are applicable for current users market.
 *
 * This method is only usable for non-IFS user.
 */
export const getGlobalPpis = createSelector(
  getPpisAll,
  ppis => ppis.filter(ppi => ppi.get('market') === MARKET_GLOBAL)
);

export const getvaraintPpiMap = state => getStateSlice(state).get('variantPpi');

export const getNewPpiAvailabelData = state =>
  getStateSlice(state).get('ppiAvailabelData');

export const getPpiId = state => getStateSlice(state).get('ppiId');

export const getCopyLocalPpis = state =>
  getStateSlice(state).get('copyLocalPpis');
export const getIsCopyLocalFetching = state =>
  getStateSlice(state).get('isCopyLocalFetching');

export const getvaraintPpi = createSelector(
  getvaraintPpiMap,
  ppisMap => ppisMap.toJS()
);

export const getGlobalPpiMap = state => getStateSlice(state).get('globalPpis');

export const getGlobalPpi = createSelector(
  getGlobalPpiMap,
  ppisMap => ppisMap.toJS()
);

/**
 * Get PPIs with a specific status. Either the inProgress or published data
 * entity needs to match the status.
 *
 * Only PPIs with current users organization / market is returned.
 *
 * @param withStatus Status to filter by.
 * @returns
 */
export const getPpisWithStatus = (withStatus, secondStatus) =>
  createSelector(
    getPpis,
    ppis => filterByStatus(ppis, withStatus, secondStatus)
  );

export const getGlobalPpisWithStatus = withStatus =>
  createSelector(
    getGlobalPpis,
    globalPpis => filterByStatus(globalPpis, withStatus)
  );

export const getPpiById = id =>
  createSelector(
    getPpisMap,
    ppisMap => ppisMap.get(id)
  );

export const getPpisByIds = (...ids) =>
  createSelector(
    getPpisMap,
    ppiMap => {
      const ppisByIds = ids.map(id => ppiMap.get(id));
      return fromJS(ppisByIds);
    }
  );

export const getPpisByItemCode = itemCode =>
  createSelector(
    getPpis,
    ppis => ppis.filter(ppi => ppi.get('itemCode') === itemCode)
  );

export const getGlobalPublishedPpisByItemCode = itemCode =>
  createSelector(
    getGlobalPpisWithStatus(States.PUBLISHED),
    globalPublishedPpis =>
      globalPublishedPpis.filter(ppi => ppi.get('itemCode') === itemCode)
  );

export const getPpiDataForId = enrichmentId =>
  createSelector(
    getPpisAll,
    ppis => {
      let ppiData = null;
      ppis.forEach(ppi => {
        const { inProgressId, publishedId } = getEnrichmentIds(ppi);
        if (inProgressId === enrichmentId) {
          ppiData = ppi.get('inProgressPPIEnrichmentData');
        }
        if (publishedId === enrichmentId) {
          ppiData = ppi.get('publishedPPIEnrichmentData');
        }
      });
      return ppiData;
    }
  );

// market ppi filter with id's

export const getMarketPpiDataForIds = enrichmentIds =>
  createSelector(
    getMarketPpisAll,
    ppis => {
      let ppiDataList = List();
      ppis.forEach(ppi => {
        const { inProgressId, publishedId } = getEnrichmentIds(ppi);
        if (enrichmentIds.includes(inProgressId)) {
          ppiDataList = ppiDataList.push(
            ppi.get('inProgressPPIEnrichmentData')
          );
        }
        if (enrichmentIds.includes(publishedId)) {
          ppiDataList = ppiDataList.push(ppi.get('publishedPPIEnrichmentData'));
        }
      });
      return ppiDataList;
    }
  );

export const getPpiDataForIds = enrichmentIds =>
  createSelector(
    getPpisAll,
    ppis => {
      let ppiDataList = List();
      ppis.forEach(ppi => {
        const { inProgressId, publishedId } = getEnrichmentIds(ppi);
        if (enrichmentIds.includes(inProgressId)) {
          ppiDataList = ppiDataList.push(
            ppi.get('inProgressPPIEnrichmentData')
          );
        }
        if (enrichmentIds.includes(publishedId)) {
          ppiDataList = ppiDataList.push(ppi.get('publishedPPIEnrichmentData'));
        }
      });
      return ppiDataList;
    }
  );

export const hasPublishedEnrichmentData = id =>
  createSelector(
    getPpisMap,
    ppiMap => {
      if (!ppiMap.has(id)) {
        return false;
      }
      const ppi = ppiMap.get(id);
      return !!ppi.get('publishedPPIEnrichmentData');
    }
  );

export const hasInProgressEnrichmentData = id =>
  createSelector(
    getPpisMap,
    ppiMap => {
      if (!ppiMap.has(id)) {
        return false;
      }
      const ppi = ppiMap.get(id);
      return !!ppi.get('inProgressPPIEnrichmentData');
    }
  );

// Returns only PPIs from published PPSI
export const getPpisForPpsiId = ppsiId =>
  createSelector(
    getPpsiById(ppsiId),
    getPpisAll,
    (ppsi, ppis) => {
      if (!ppsi || ppis.isEmpty()) {
        return List();
      }
      const ppiDetails = ppsi.getIn([
        'publishedPpsiEnrichmentData',
        'ppiDetails'
      ]);
      if (!ppiDetails) {
        return List();
      }
      const enrichmentIds = ppiDetails.map(detail =>
        detail.get('ppiEnrichmentDataId')
      );
      return ppis.filter(ppi =>
        enrichmentIds.includes(ppi.getIn(['publishedPPIEnrichmentData', 'id']))
      );
    }
  );

// global Ppi local Market
export const getGlobalPublishedPpiIfsList = state =>
  getStateSlice(state).get('globalPublishedPpiIfs');
//get global ppi variant
//global Ppi local Market
export const getGlobalPpiVariant = state =>
  getStateSlice(state).get('globalPpiVariant');

//global Ppi local Market
export const isFetchingGlobalPpiVariant = state =>
  getStateSlice(state).get('fetchGlobalPpiVariant');

//global Ppi local Market
export const isFetchingGlobalPpiVariantError = state =>
  getStateSlice(state).get('fetchGlobalPpiVariantError');

const FilterPredicate = {
  notUpToDate: () => ppi => {
    const status = ppi.get('status');
    if (status === 'PUBLISHED' || status === 'UNPUBLISHED') {
      const publishedPpsiData = ppi.get('publishedPPIEnrichmentData');
      if (publishedPpsiData && publishedPpsiData.get('upToDate') === false) {
        return true;
      }
      const inProgressPpsiData = ppi.get('inProgressPPIEnrichmentData');
      if (inProgressPpsiData && publishedPpsiData.get('upToDate') === false) {
        return true;
      }
    } else {
      const inProgressPpsiData = ppi.get('inProgressPPIEnrichmentData');
      if (inProgressPpsiData && inProgressPpsiData.get('upToDate') === false) {
        return true;
      }
    }
    return false;
  },
  notUpToDatePublished: () => ppi => {
    const status = ppi.get('status');
    if (status === 'PUBLISHED') {
      const publishedPpsiData = ppi.get('publishedPPIEnrichmentData');
      if (publishedPpsiData && publishedPpsiData.get('upToDate') === false) {
        return true;
      }
    }
    return false;
  }
};

export const getPpisNotUptoDate = () =>
  createSelector(
    getPpis,
    ppis => {
      return ppis.filter(FilterPredicate.notUpToDate());
    }
  );

export const getIsFetching = state => getStateSlice(state).get('isFetching');
export const getIsFetchingPpis = state =>
  getStateSlice(state).get('isFetchingRelatedPpis');
export const getRelatedPpis = state => getStateSlice(state).get('relatedPpis');

export const getIsInDraftFetched = state =>
  getStateSlice(state).get('isInDraftFetched');

export const getFetchError = state => getStateSlice(state).get('fetchError');

export const getIsFetchingGlobal = state =>
  getStateSlice(state).get('isFetchingGlobal');

export const getFetchGlobalError = state =>
  getStateSlice(state).get('fetchGlobalError');

export const getIsSaving = state => getStateSlice(state).get('isSaving');

export const getLastSavedPpiId = state =>
  getStateSlice(state).get('lastSavedPpiId');

export const getIsPublishing = state =>
  getStateSlice(state).get('isPublishing');

export const getPublishError = state =>
  getStateSlice(state).get('publishError');

export const getIsUnpublishing = state =>
  getStateSlice(state).get('isUnpublishing');

export const getUnpublishError = state =>
  getStateSlice(state).get('unpublishError');

export const getIsMarkingAsUptoDate = state =>
  getStateSlice(state).get('isMarkingAsUptoDate');

export const getMarkAsUptoDateError = state =>
  getStateSlice(state).get('MarkAsUptoDateError');

export const getIsCreatingDraft = state =>
  getStateSlice(state).get('isCreatingDraft');

export const getCreateDraftError = state =>
  getStateSlice(state).get('createDraftError');

export const getIsImportingPpiUpdate = state =>
  getStateSlice(state).get('isImportingPpiUpdate');

export const getImportUpdatePpiError = state =>
  getStateSlice(state).get('importPpiUpdateError');

// PPI Smart update changes;

export const getIsImportingGlobalPpi = state =>
  getStateSlice(state).get('isImportingGlobalPpiUpdate');

export const getImportGlobalPpiError = state =>
  getStateSlice(state).get('importGlobalPpiUpdateError');

// PPI discard draft

export const getIsDiscardingDraft = state =>
  getStateSlice(state).get('isdiscardingDraft');

export const getDicardDraftError = state =>
  getStateSlice(state).get('discardDraftError');

export const getMarketPpisAll = state => getStateSlice(state).get('marketPpis');

// export const getGlobalPpi = state => getStateSlice(state).get('globalPpis');
export const getfetchingGlobalPpi = state =>
  getStateSlice(state).get('fetchingGlobalPpis');
export const getGlobalPpiError = state =>
  getStateSlice(state).get('fetchGlobalPpisError');

export const getfetchingVariantPpi = state =>
  getStateSlice(state).get('fetchingVariantPpi');
export const getfetchVariantPpiError = state =>
  getStateSlice(state).get('fetchVariantPpiError');

// Fetch global PPi IFS Market for local market
export const getGlobalPublishedPpiIfs = createSelector(
  getGlobalPublishedPpiIfsList,
  ppsiMap => ppsiMap.toList()
);
